// Herramienta interna superadmin para capturar screenshots de URLs externas.
// Útil para portafolio + casos. Sidecar `focus_screenshot` (Playwright) detrás
// de https://screenshot.lifefocus.agency. Auth con JWT del admin.
//
// Flujo:
//   1. Ingresar URL.
//   2. "Cargar previsualización" → sidecar hace fullPage de desktop + mobile.
//   3. En el visor el user hace scroll y aprieta "Capturar visible". Hacemos
//      crop client-side con canvas (no requiere otra request al sidecar).
//   4. Alternativa rápida: botones "Above the fold" capturan el viewport
//      arriba de la página sin pasar por el preview.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera,
  Download,
  Monitor,
  Smartphone,
  Loader2,
  AlertCircle,
  Eye,
  Crop,
} from 'lucide-react';
import { pb, currentUser } from '~/lib/pb';
import styles from './_authed.screenshots.module.css';

const SCREENSHOT_URL =
  import.meta.env.VITE_SCREENSHOT_URL || 'https://screenshot.lifefocus.agency';

export const Route = createFileRoute('/_authed/screenshots')({
  beforeLoad: () => {
    const me = currentUser();
    if (me?.role !== 'superadmin') {
      throw redirect({ to: '/' });
    }
  },
  component: ScreenshotsPage,
});

type Preset = 'desktop' | 'mobile';
type Format = 'webp' | 'png' | 'jpeg';

// Dimensiones de captura en pixels reales (deben coincidir con VIEWPORTS del sidecar).
const VIEWPORT = {
  desktop: { width: 1440, height: 900 },
  mobile: { width: 390, height: 844 },
} as const;

interface CaptureResult {
  preset: Preset;
  source: 'direct' | 'crop';
  blobUrl: string;
  bytes: number;
  ms: number | null;
  filename: string;
  width: number;
  height: number;
}

interface PreviewState {
  preset: Preset;
  blobUrl: string;
  imgWidth: number;
  imgHeight: number;
}

function ScreenshotsPage() {
  const [url, setUrl] = useState('');
  const [format, setFormat] = useState<Format>('webp');
  const [quality, setQuality] = useState<number>(85);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [results, setResults] = useState<CaptureResult[]>([]);
  const [previews, setPreviews] = useState<PreviewState[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reset cuando cambia la URL
  useEffect(() => {
    setPreviews((prev) => {
      for (const p of prev) URL.revokeObjectURL(p.blobUrl);
      return [];
    });
  }, [url]);

  async function callSidecar(
    preset: Preset,
    opts: { fullPage?: boolean; scrollY?: number },
  ): Promise<{ blob: Blob; ms: number } | null> {
    const token = pb.authStore.token;
    if (!token) {
      setError('No estás autenticado.');
      return null;
    }
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setError('Ingresa una URL.');
      return null;
    }
    const t0 = performance.now();
    const res = await fetch(`${SCREENSHOT_URL}/screenshot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        url: trimmedUrl,
        preset,
        fullPage: !!opts.fullPage,
        scrollY: opts.scrollY ?? 0,
        format,
        quality,
      }),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
    }
    const blob = await res.blob();
    const msHeader = res.headers.get('X-Capture-Ms');
    const ms = msHeader ? Number(msHeader) : Math.round(performance.now() - t0);
    return { blob, ms };
  }

  function filenameFor(preset: Preset, kind: 'fold' | 'full' | 'crop'): string {
    const hostname = (() => {
      try {
        return new URL(url.trim()).hostname.replace(/^www\./, '');
      } catch {
        return 'snapshot';
      }
    })();
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `${hostname}-${preset}-${kind}-${stamp}.${format}`;
  }

  async function captureDirect(preset: Preset, fullPage: boolean) {
    setError(null);
    const label = `${preset}-${fullPage ? 'full' : 'fold'}`;
    setBusyLabel(label);
    try {
      const r = await callSidecar(preset, { fullPage });
      if (!r) return;
      const blobUrl = URL.createObjectURL(r.blob);
      const { width, height } = await getImgSize(blobUrl);
      const result: CaptureResult = {
        preset,
        source: 'direct',
        blobUrl,
        bytes: r.blob.size,
        ms: r.ms,
        filename: filenameFor(preset, fullPage ? 'full' : 'fold'),
        width,
        height,
      };
      setResults((prev) => [result, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyLabel(null);
    }
  }

  async function loadPreviews() {
    setError(null);
    setBusyLabel('preview');
    try {
      const [desktop, mobile] = await Promise.all([
        callSidecar('desktop', { fullPage: true }),
        callSidecar('mobile', { fullPage: true }),
      ]);
      const fresh: PreviewState[] = [];
      for (const [preset, r] of [['desktop', desktop] as const, ['mobile', mobile] as const]) {
        if (!r) continue;
        const blobUrl = URL.createObjectURL(r.blob);
        const { width, height } = await getImgSize(blobUrl);
        fresh.push({ preset, blobUrl, imgWidth: width, imgHeight: height });
      }
      setPreviews((prev) => {
        for (const p of prev) URL.revokeObjectURL(p.blobUrl);
        return fresh;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyLabel(null);
    }
  }

  function captureCrop(preset: Preset, blobUrl: string, cropPx: { y: number; height: number }) {
    const img = new Image();
    img.onload = () => {
      const vw = VIEWPORT[preset].width;
      const vh = VIEWPORT[preset].height;
      const canvas = document.createElement('canvas');
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Imagen viene a ancho `vw` (resolved at viewport size by Playwright +
      // deviceScaleFactor x2/x3 → la imagen real es vw*scale × pageHeight*scale).
      // Para crop, dibujamos manteniendo escala 1:1 al device pixel del preset.
      // Cropear region [0, cropY, vw*scale, vh*scale] del img y dibujar a vw×vh.
      const scaleX = img.width / vw;
      const srcY = cropPx.y * scaleX;
      const srcH = cropPx.height * scaleX;
      ctx.drawImage(img, 0, srcY, img.width, srcH, 0, 0, vw, vh);
      const mime =
        format === 'webp' ? 'image/webp' : format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const q = format === 'png' ? undefined : quality / 100;
      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          const u = URL.createObjectURL(blob);
          const r: CaptureResult = {
            preset,
            source: 'crop',
            blobUrl: u,
            bytes: blob.size,
            ms: null,
            filename: filenameFor(preset, 'crop'),
            width: vw,
            height: vh,
          };
          setResults((prev) => [r, ...prev]);
        },
        mime,
        q,
      );
    };
    img.src = blobUrl;
  }

  function clearAll() {
    for (const r of results) URL.revokeObjectURL(r.blobUrl);
    setResults([]);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={`h2 ${styles.title}`}>Screenshots</h1>
      </header>

      <section className={styles.form}>
        <label className={styles.field}>
          <span className={styles.field__label}>URL</span>
          <input
            type="url"
            className={styles.input}
            placeholder="https://krb.cl/"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={!!busyLabel}
            autoFocus
          />
        </label>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.field__label}>Formato</span>
            <select
              className={styles.input}
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
              disabled={!!busyLabel}
            >
              <option value="webp">WebP (recomendado)</option>
              <option value="jpeg">JPEG</option>
              <option value="png">PNG</option>
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.field__label}>Calidad ({quality})</span>
            <input
              type="range"
              min={50}
              max={100}
              step={5}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              disabled={!!busyLabel || format === 'png'}
            />
          </label>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            onClick={() => captureDirect('desktop', false)}
            disabled={!!busyLabel || !url.trim()}
            title="Captura el viewport above-the-fold desktop"
          >
            <BusyOrIcon
              busy={busyLabel === 'desktop-fold'}
              Icon={Monitor}
            />
            Desktop above-fold
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={() => captureDirect('mobile', false)}
            disabled={!!busyLabel || !url.trim()}
            title="Captura el viewport above-the-fold mobile"
          >
            <BusyOrIcon
              busy={busyLabel === 'mobile-fold'}
              Icon={Smartphone}
            />
            Mobile above-fold
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={() => captureDirect('desktop', true)}
            disabled={!!busyLabel || !url.trim()}
            title="Página entera desktop (puede ser muy alta)"
          >
            <BusyOrIcon busy={busyLabel === 'desktop-full'} Icon={Camera} />
            Desktop full
          </button>
          <button
            type="button"
            className={styles.btn}
            onClick={() => captureDirect('mobile', true)}
            disabled={!!busyLabel || !url.trim()}
            title="Página entera mobile"
          >
            <BusyOrIcon busy={busyLabel === 'mobile-full'} Icon={Camera} />
            Mobile full
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={loadPreviews}
            disabled={!!busyLabel || !url.trim()}
            title="Carga el sitio completo y permite elegir visualmente qué cropear"
          >
            <BusyOrIcon busy={busyLabel === 'preview'} Icon={Eye} />
            Previsualizar y cropear
          </button>
        </div>

        {error && (
          <div className={styles.error} role="alert">
            <AlertCircle size={14} aria-hidden />
            <span>{error}</span>
          </div>
        )}
      </section>

      {previews.length > 0 && (
        <section className={styles.preview}>
          <header className={styles.previewHeader}>
            <h2 className="h3">Previsualizador</h2>
            <p className={styles.dim}>
              Hace scroll dentro de cada visor y aprieta el botón para capturar
              la región visible al tamaño del viewport del preset.
            </p>
          </header>
          <div className={styles.previewGrid}>
            {previews.map((p) => (
              <PreviewViewer
                key={p.preset}
                preview={p}
                onCapture={(scrollY) =>
                  captureCrop(p.preset, p.blobUrl, {
                    y: scrollY,
                    height: VIEWPORT[p.preset].height,
                  })
                }
              />
            ))}
          </div>
        </section>
      )}

      {results.length > 0 && (
        <section className={styles.results}>
          <header className={styles.resultsHeader}>
            <h2 className="h3">Capturas</h2>
            <button type="button" className={styles.btnGhost} onClick={clearAll}>
              Limpiar todo
            </button>
          </header>

          <div className={styles.grid}>
            {results.map((r) => (
              <article key={r.blobUrl} className={styles.card}>
                <div className={styles.cardMedia}>
                  <img src={r.blobUrl} alt={`${r.preset} screenshot`} />
                </div>
                <footer className={styles.cardFooter}>
                  <div className={styles.cardMeta}>
                    {r.preset === 'desktop' ? (
                      <Monitor size={12} aria-hidden />
                    ) : (
                      <Smartphone size={12} aria-hidden />
                    )}
                    <span>{r.preset}</span>
                    <span className={styles.tag}>{r.source}</span>
                    <span className={styles.dim}>
                      {r.width}×{r.height} · {formatBytes(r.bytes)}
                      {r.ms ? ` · ${r.ms}ms` : ''}
                    </span>
                  </div>
                  <a
                    className={styles.btn}
                    href={r.blobUrl}
                    download={r.filename}
                  >
                    <Download size={14} aria-hidden />
                    Descargar
                  </a>
                </footer>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function BusyOrIcon({
  busy,
  Icon,
}: {
  busy: boolean;
  Icon: typeof Monitor;
}) {
  return busy ? (
    <Loader2 size={14} className={styles.spin} aria-hidden />
  ) : (
    <Icon size={14} aria-hidden />
  );
}

interface PreviewViewerProps {
  preview: PreviewState;
  onCapture: (scrollY: number) => void;
}

function PreviewViewer({ preview, onCapture }: PreviewViewerProps) {
  // El visor muestra la imagen escalada al ancho disponible. Mantenemos un ref
  // al contenedor scrollable y un ref a la imagen para calcular el scroll
  // efectivo en pixels del viewport original.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const vw = VIEWPORT[preview.preset].width;
  const vh = VIEWPORT[preview.preset].height;

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth));
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // La imagen tiene resolución `preview.imgWidth × preview.imgHeight` (con
  // deviceScaleFactor del preset, por eso es ~2x/3x el viewport). En CSS la
  // queremos al ancho del container, manteniendo aspect ratio.
  const renderWidth = containerWidth || vw;
  const scale = renderWidth / vw;
  const renderViewportHeight = vh * scale;

  function handleCapture() {
    const el = wrapRef.current;
    if (!el) return;
    // scrollTop en CSS px = scrollY en viewport px * scale
    const cssScroll = el.scrollTop;
    const viewportScroll = Math.max(0, Math.round(cssScroll / scale));
    onCapture(viewportScroll);
  }

  const Icon = preview.preset === 'desktop' ? Monitor : Smartphone;

  return (
    <div className={styles.viewer}>
      <header className={styles.viewerHeader}>
        <span className={styles.viewerLabel}>
          <Icon size={14} aria-hidden />
          {preview.preset} · {vw}×{vh}
        </span>
        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleCapture}
        >
          <Crop size={14} aria-hidden />
          Capturar visible
        </button>
      </header>
      <div
        ref={wrapRef}
        className={styles.viewerScroll}
        style={{ height: renderViewportHeight }}
      >
        <img
          src={preview.blobUrl}
          alt={`Preview ${preview.preset}`}
          className={styles.viewerImage}
          style={{ width: renderWidth }}
          draggable={false}
        />
      </div>
      <p className={styles.viewerHint}>
        Lo que ves en este recuadro es exactamente lo que se va a capturar
        ({vw}×{vh}). Hace scroll y aprieta &quot;Capturar visible&quot;.
      </p>
    </div>
  );
}

function getImgSize(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = src;
  });
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}
